import React, { useState, useRef, useEffect } from 'react';
import { PlusCircle, Upload, History, BookOpen, LogOut, Globe, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/toaster';
import MyCourses from './MyCourses';

// SVG Flag components for countries
const FlagGB = ({ className = "w-5 h-4" }) => (
  <svg viewBox="0 0 640 480" className={className} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <clipPath id="gb-a">
        <path fillOpacity=".7" d="M-85.3 0h682.6v512h-682.6z"/>
      </clipPath>
    </defs>
    <g clipPath="url(#gb-a)" transform="translate(80) scale(.94)">
      <g strokeWidth="1pt">
        <path fill="#006" d="M-256 0H768v512H-256z"/>
        <path fill="#fff" d="M-256 0v57.2L653.5 512H768v-57.2L-141.5 0H-256zM768 0v57.2L-141.5 512H-256v-57.2L653.5 0H768z"/>
        <path fill="#fff" d="M170.7 0v512h170.6V0H170.7zM-256 170.7v170.6H768V170.7H-256z"/>
        <path fill="#c00" d="M-256 204.8v102.4H768V204.8H-256zM204.8 0v512h102.4V0H204.8zM-256 512L85.3 341.3h76.4l-170.7 170.6H-256zM-256 0L85.3 170.7H8.9L-256 38.1V0zm606.4 170.7L768 0h76.8L606.4 170.7h-76.8zM768 512L341.3 307.2v-76.8L768 512z"/>
      </g>
    </g>
  </svg>
);

const FlagGR = ({ className = "w-5 h-4" }) => (
  <svg viewBox="0 0 27 18" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Blue canton with white cross in upper hoist corner */}
    <rect x="0" y="0" width="8" height="8" fill="#005BAE"/>
    <rect x="3" y="0" width="2" height="8" fill="#FFFFFF"/>
    <rect x="0" y="3" width="8" height="2" fill="#FFFFFF"/>
    
    {/* Nine alternating horizontal stripes (5 blue, 4 white) */}
    <rect x="8" y="0" width="19" height="2" fill="#005BAE"/> {/* Blue 1 */}
    <rect x="8" y="2" width="19" height="2" fill="#FFFFFF"/> {/* White 1 */}
    <rect x="8" y="4" width="19" height="2" fill="#005BAE"/> {/* Blue 2 */}
    <rect x="8" y="6" width="19" height="2" fill="#FFFFFF"/> {/* White 2 */}
    <rect x="8" y="8" width="19" height="2" fill="#005BAE"/> {/* Blue 3 */}
    <rect x="0" y="10" width="27" height="2" fill="#FFFFFF"/> {/* White 3 */}
    <rect x="0" y="12" width="27" height="2" fill="#005BAE"/> {/* Blue 4 */}
    <rect x="0" y="14" width="27" height="2" fill="#FFFFFF"/> {/* White 4 */}
    <rect x="0" y="16" width="27" height="2" fill="#005BAE"/> {/* Blue 5 */}
  </svg>
);

// Supported languages for the UI/chat interaction (restricted to English and Greek)
const SUPPORTED_LANGUAGES = [
  { code: 'English', label: 'English', countryCode: 'GB', Flag: FlagGB },
  { code: 'Greek', label: 'Ελληνικά', countryCode: 'GR', Flag: FlagGR },
];

/**
 * Landing Choice Component
 * Entry point where users choose between creating from scratch or importing
 * Now includes tabs for "New Course" and "My Courses"
 */
const LandingChoice = ({ onCreateNew, onImport }) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'history'
  // Initialize selectedLanguage based on current i18n language
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return i18n.language === 'el' ? 'Greek' : 'English';
  }); // Chat language (for AI interaction) - also controls UI language
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, logout } = useAuth();

  // Handle UI language change (separate from chat language)
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    setIsLanguageDropdownOpen(false);
    // Change UI language based on selection
    if (language === 'Greek') {
      i18n.changeLanguage('el');
    } else {
      i18n.changeLanguage('en');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedLang = SUPPORTED_LANGUAGES.find(lang => lang.code === selectedLanguage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-pink-50 to-teal-100 px-4 py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Top bar: tabs + logout */}
        <div className="flex items-center justify-between gap-4">
          <div className="bg-white rounded-lg shadow-md p-1 inline-flex">
              <button
              onClick={() => setActiveTab('new')}
              className={`flex items-center gap-2 px-6 py-3 rounded-md font-semibold transition-all ${
                activeTab === 'new'
                  ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <BookOpen className="w-5 h-5" />
              {t('landing.newCourse')}
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex items-center gap-2 px-6 py-3 rounded-md font-semibold transition-all ${
                activeTab === 'history'
                  ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <History className="w-5 h-5" />
              {t('landing.myCourses')}
            </button>
          </div>

          <div className="flex items-center gap-3">
            {/* Language Selector - controls both UI language and chat language */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 rounded-xl shadow-md hover:shadow-lg px-4 py-2.5 transition-all duration-200 border border-gray-100 hover:border-gray-200 min-w-[140px]"
                title={t('landing.chatLanguage')}
              >
                <Globe className="w-4 h-4 text-gray-500" />
                {selectedLang && (
                  <>
                    <selectedLang.Flag className="w-5 h-4 rounded-sm shadow-sm" />
                    <span className="text-gray-800 font-semibold text-sm">{selectedLang.countryCode}</span>
                    <span className="text-gray-700 font-medium text-sm">{selectedLang.label}</span>
                  </>
                )}
                <ChevronDown 
                  className={`w-4 h-4 text-gray-500 ml-auto transition-transform duration-200 ${
                    isLanguageDropdownOpen ? 'transform rotate-180' : ''
                  }`} 
                />
              </button>
              
              {/* Dropdown Menu */}
              {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  {SUPPORTED_LANGUAGES.map((lang) => {
                    const isSelected = lang.code === selectedLanguage;
                    const FlagComponent = lang.Flag;
                    return (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${
                          isSelected
                            ? 'bg-gradient-to-r from-teal-50 to-blue-50 text-teal-700 font-semibold border-l-4 border-teal-500'
                            : 'text-gray-700 hover:bg-gray-50 font-medium'
                        }`}
                      >
                        <FlagComponent className="w-6 h-5 rounded-sm shadow-sm flex-shrink-0" />
                        <div className="flex flex-col">
                          <span className={`text-sm ${isSelected ? 'text-teal-700' : 'text-gray-800'}`}>
                            {lang.countryCode} {lang.label}
                          </span>
                          <span className={`text-xs ${isSelected ? 'text-teal-600' : 'text-gray-500'}`}>
                            {lang.code}
                          </span>
                        </div>
                        {isSelected && (
                          <svg
                            className="w-5 h-5 text-teal-600 ml-auto"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {user && (
              <>
                <div className="text-right">
                  <p className="text-sm text-gray-600">{t('common.loggedInAs')}</p>
                  <p className="text-sm font-semibold text-gray-800">{user.username}</p>
                </div>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-100 text-gray-700 rounded-lg font-medium transition-colors shadow-md"
                >
                  <LogOut className="w-4 h-4" />
                  {t('common.logout')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Title centered below top bar */}
        <div className="text-center">
          <h1 className="text-5xl font-extrabold bg-gradient-to-r from-orange-600 via-pink-600 to-teal-600 bg-clip-text text-transparent mb-3">
            {t('landing.welcome')}
          </h1>
          <p className="text-xl text-gray-600">
            {activeTab === 'new' ? t('landing.getStarted') : t('landing.courseHistory')}
          </p>
        </div>

        {/* Tab Content */}
        {activeTab === 'new' ? (
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Create from Scratch */}
              <div
                className="group bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-orange-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-pink-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <PlusCircle className="w-12 h-12 text-orange-600" />
                  </div>

                  <h2 className="text-2xl font-bold text-gray-800 mb-3">
                    {t('landing.createFromScratch')}
                  </h2>

                  <p className="text-gray-600 mb-6">
                    {t('landing.createDescription')}
                  </p>

                  <div className="space-y-2 text-sm text-left w-full">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">{t('landing.guidedDesign')}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">{t('landing.aiContent')}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">{t('landing.chatSupport')}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">{t('landing.versionControl')}</span>
                    </div>
                  </div>

                  <div className="mt-6 w-full">
                    <button
                      onClick={() => onCreateNew(selectedLanguage)}
                      className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-pink-600 transition-all"
                    >
                      {t('landing.startCreating')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Import Existing */}
              <button
                onClick={() => onImport(selectedLanguage)}
                className="group bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-teal-300"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-teal-100 to-pink-100 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Upload className="w-12 h-12 text-teal-600" />
                  </div>
                  
                  <h2 className="text-2xl font-bold text-gray-800 mb-3">
                    {t('landing.importImprove')}
                  </h2>
                  
                  <p className="text-gray-600 mb-6">
                    {t('landing.importDescription')}
                  </p>
                  
                  <div className="space-y-2 text-sm text-left w-full">
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">{t('landing.uploadFiles')}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">{t('landing.qualityAnalysis')}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">{t('landing.improvementPlans')}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-gray-700">{t('landing.frameworks')}</span>
                    </div>
                  </div>

                  <div className="mt-6 w-full">
                    <span className="block w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-pink-500 text-white rounded-lg font-semibold group-hover:from-teal-600 group-hover:to-pink-600 transition-all text-center">
                      {t('landing.importCourse')}
                    </span>
                  </div>
                </div>
              </button>
            </div>

            {/* Info Footer */}
            <div className="mt-12 text-center">
              <p className="text-gray-500 text-sm">
                {t('landing.footerInfo')}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <MyCourses />
          </div>
        )}
      </div>
      <Toaster />
    </div>
  );
};

export default LandingChoice;


